import { TSmartAccountClient } from '@/types';
import { useEffect, useRef, useState } from 'react';
import { useCurrentPrivyWallet } from './useCurrentPrivyWallet';
import { updateCommentSession, getCommentAccountSession } from '@/db/queries';
import { Hex, toHex } from 'viem';
import hkdf from 'futoin-hkdf';
import {
  COMMENT_SIGN_IN_MESSAGE,
  HKDF_KEY_INFO,
  RANDOM_USER_NAME_FOR_COMMENT,
} from '@/utils/constants';
import { privateKeyToAccount } from 'viem/accounts';
import {
  getSmartAccountClient,
  privyWalletToClient,
} from '@/utils/account-utils';
import { captureException } from '@sentry/nextjs';
import { toast } from '@fileverse/ui';
import {
  ConnectedWallet,
  usePrivy,
  useLogin as usePrivyLogin,
} from '@privy-io/react-auth';
import { hasValidAuth, hasValidCommentAuth } from '@/utils/auth-utils';
import { AgentInstance } from '@/utils/identity-utils/smart-account-agent';
import { useAccountContext } from '@/components/providers/account-provider';
import { usePlausibleEvents } from './use-plausible-events';

const useCommentAccount = (setCollaboratorName: (name: string) => void) => {
  const { currentWallet, authenticated } = useCurrentPrivyWallet();
  const [loading, setLoading] = useState(false);
  const { logout, authenticated: privyAuthenticated } = usePrivy();
  const { identityInitialized } = useAccountContext();
  const loginRef = useRef(false);
  const { onSignUpPlausible, onLogInPlausible } = usePlausibleEvents();
  const { login } = usePrivyLogin({
    onComplete: async ({ isNewUser }) => {
      setTimeout(async () => {
        if (loginRef.current) {
          await signLoginMessage(currentWallet!);
          if (isNewUser) {
            onSignUpPlausible();
          } else {
            onLogInPlausible();
          }
        }
      }, 0);
    },
  });

  const [smartAccountClient, setSmartAccountClient] =
    useState<TSmartAccountClient | null>(null);

  const generateAccount = async (signedMessage: string) => {
    const derivedKey = hkdf(Buffer.from(signedMessage), 32, {
      info: Buffer.from(HKDF_KEY_INFO),
    });
    //@ts-ignore
    const privateAccount = privateKeyToAccount(toHex(derivedKey));

    const _smartAccountClient = await getSmartAccountClient(privateAccount);
    return _smartAccountClient;
  };

  const generateRandomMessage = () => {
    const cryptoArray = new Uint8Array(64);
    return window.crypto.getRandomValues(cryptoArray).toString();
  };

  const createRandomAccount = async (randomUser: string) => {
    try {
      setLoading(true);
      if (!randomUser) return;
      const session = await getCommentAccountSession(randomUser as Hex);
      let signedMsg = session?.password;
      if (!signedMsg) {
        signedMsg = generateRandomMessage();
      }
      const account = await generateAccount(signedMsg);
      setSmartAccountClient(account);
      localStorage.setItem(RANDOM_USER_NAME_FOR_COMMENT, randomUser);
      await updateCommentSession(randomUser, signedMsg);
    } catch (error: any) {
      captureException(error?.message, {
        level: 'error',
        extra: {
          message: 'Failed to generate random comment account',
        },
      });
      toast({
        description:
          error?.message || 'Failed to generate random comment account',
        variant: 'danger',
        hasIcon: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const [shouldAutoLoginUser, setAutoLoginUser] = useState(false);

  useEffect(() => {
    if (shouldAutoLoginUser && !authenticated) {
      setAutoLoginUser(false);
      login();
    }
  }, [shouldAutoLoginUser, authenticated, login]);
  const initailiseAccountIfFound = async (username: string) => {
    const userAccountData = await getCommentAccountSession(username);
    if (!userAccountData || !userAccountData.password) {
      setSmartAccountClient(null);
      return;
    }
    setLoading(true);
    const signedMsg = userAccountData.password;
    const account = await generateAccount(signedMsg);
    setCollaboratorName(username);
    setSmartAccountClient(account);
    setLoading(false);
  };

  const signLoginMessage = async (wallet: ConnectedWallet) => {
    try {
      if (!wallet) {
        if (!authenticated) return;
        await logout();
        setAutoLoginUser(true);
        return;
      }
      setLoading(true);
      if (await hasValidAuth(wallet.address as Hex)) return;
      const client = await privyWalletToClient(wallet);
      const signedMessage = await client.signMessage({
        message: COMMENT_SIGN_IN_MESSAGE,
      });

      await updateCommentSession(wallet.address as Hex, signedMessage);
      setCollaboratorName(wallet.address);
      const account = await generateAccount(signedMessage);
      setSmartAccountClient(account);
    } catch (err) {
      console.error(err);
      toast({
        description: 'Failed to sign message',
        variant: 'danger',
        hasIcon: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const isLoggedin = privyAuthenticated && authenticated;

  const handleAccountInitialization = async () => {
    if (identityInitialized && currentWallet) {
      setCollaboratorName(currentWallet?.address!);
      setSmartAccountClient(AgentInstance.getSmartAccountAgent());
      return;
    }
    const address = isLoggedin ? currentWallet?.address : '';
    const username =
      address || localStorage.getItem(RANDOM_USER_NAME_FOR_COMMENT);
    if (!username) return;
    await initailiseAccountIfFound(username);
  };
  useEffect(() => {
    handleAccountInitialization();
  }, [isLoggedin, identityInitialized]);

  const connectViaWallet = async () => {
    if (isLoggedin) {
      const walletAddress = currentWallet?.address;
      const userAccountData = await getCommentAccountSession(walletAddress!);
      if (!userAccountData || !userAccountData.password) {
        if (!currentWallet) {
          if (!authenticated) return;
          await logout();
          setAutoLoginUser(true);
          return;
        }
        setLoading(true);

        if (await hasValidCommentAuth(walletAddress as Hex)) return;
        const client = await privyWalletToClient(currentWallet);

        const signedMessage = await client.signMessage({
          message: COMMENT_SIGN_IN_MESSAGE,
        });
        await updateCommentSession(walletAddress as Hex, signedMessage);

        setCollaboratorName(walletAddress!);

        const account = await generateAccount(signedMessage);
        setSmartAccountClient(account);
      } else {
        setCollaboratorName(walletAddress!);
        const account = await generateAccount(userAccountData.password);
        setSmartAccountClient(account);
      }
    } else if (privyAuthenticated && !authenticated) {
      await logout().catch(console.error);
      setAutoLoginUser(true);
    } else {
      loginRef.current = true;
      login();
    }
  };
  return {
    isConnected: !!smartAccountClient,
    createRandomAccount,
    smartAccountClient,
    isLoading: loading,
    connectViaWallet,
  };
};

export default useCommentAccount;
